import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    previewPaymentReport, importPaymentReport, fetchPaymentImports, fetchCommissionPayments,
} from '../../api';
import DataGrid from '../common/DataGrid';
import { formatMoney, moneyColumn, toISODate } from '../marketing/shared';
import { caseColumn, formatPaidAt } from './shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Commissions.css';

// Payment Imports: upload the IRS Logics payment report (the UTF-16 .csv
// Logics exports, or an Excel file). The server reads it first and says what
// it found: how many payments and refunds, how many are new, which statuses
// it skipped, which cases name an opener but have no split agent yet, and
// which settlement officers it will add to the agents list. Nothing is saved
// until Import is pressed, and uploading the same rows again never counts a
// payment twice.

const TREATMENT_LABEL = {
    payment: 'Counted as payment',
    refund: 'Counted as refund',
    skipped: 'Skipped (no money moved)',
    unknown: 'Skipped (unrecognized)',
};

const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${toISODate(d)} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

function Stat({ label, value, sub }) {
    return (
        <div className="com__stat">
            <div className="com__stat-label">{label}</div>
            <div className="com__stat-value">{value}</div>
            {sub && <div className="com__stat-sub">{sub}</div>}
        </div>
    );
}

function Summary({ summary, onAddSplit, onOpenCase }) {
    const unknown = summary.statuses.filter((s) => s.treatment === 'unknown');
    return (
        <>
            <div className="com__stats">
                <Stat label="Rows in file" value={summary.data_rows} />
                <Stat label="Payments" value={summary.payment_rows} sub={formatMoney(summary.payment_total)} />
                <Stat label="Refunds" value={summary.refund_rows} sub={formatMoney(summary.refund_total)} />
                <Stat label="New" value={summary.new_rows} sub={`${summary.known_rows} already imported`} />
                <Stat label="Skipped" value={summary.skipped_rows} />
            </div>

            {unknown.length > 0 && (
                <p className="mkt__error">
                    {unknown.length === 1 ? 'One status wasn\'t' : `${unknown.length} statuses weren't`} recognized,
                    so {unknown.length === 1 ? 'its rows were' : 'their rows were'} skipped:{' '}
                    <strong>{unknown.map((s) => s.status || '(blank)').join(', ')}</strong>. If any of these is
                    money received or refunded, tell the developer so it can be counted.
                </p>
            )}

            <h3 className="com__section-title">Payment statuses in this file</h3>
            <div className="com__table-wrap">
                <table className="com__table">
                    <thead>
                        <tr><th>Status</th><th>Treatment</th><th className="com__num">Rows</th><th className="com__num">Amount</th></tr>
                    </thead>
                    <tbody>
                        {summary.statuses.map((s) => (
                            <tr key={s.status}>
                                <td>{s.status || '(blank)'}</td>
                                <td><span className={`com__tag com__tag--${s.treatment}`}>{TREATMENT_LABEL[s.treatment]}</span></td>
                                <td className="com__num">{s.rows}</td>
                                <td className="com__num">
                                    {s.treatment === 'payment' || s.treatment === 'refund' ? formatMoney(s.amount_cents / 100) : ''}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {summary.error_count > 0 && (
                <>
                    <h3 className="com__section-title">
                        {summary.error_count} row{summary.error_count === 1 ? '' : 's'} couldn't be read and will be skipped
                    </h3>
                    <div className="com__table-wrap">
                        <table className="com__table">
                            <thead><tr><th>Line</th><th>Problem</th></tr></thead>
                            <tbody>
                                {summary.errors.map((e) => (
                                    <tr key={e.line}><td>{e.line}</td><td>{e.message}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {summary.new_officers.length > 0 && (
                <p className="mkt__panel-hint">
                    New settlement officer{summary.new_officers.length === 1 ? '' : 's'}, added to Agents on import:{' '}
                    <strong>{summary.new_officers.join(', ')}</strong>. If one is someone already on the list under
                    another spelling, rename them there to match Logics first.
                </p>
            )}

            {summary.unentered_cases > 0 && (
                <p className="mkt__panel-hint">
                    {summary.unentered_cases} case{summary.unentered_cases === 1 ? ' in this file hasn\'t' : 's in this file haven\'t'}{' '}
                    been entered on the Splits page. Until one is, its settlement officer earns 20% of every payment,
                    with no cap and no split agents.
                </p>
            )}

            {summary.multi_invoice_cases.length > 0 && (
                <p className="mkt__panel-hint">
                    {summary.multi_invoice_cases.length === 1 ? 'One case' : `${summary.multi_invoice_cases.length} cases`} in
                    this file {summary.multi_invoice_cases.length === 1 ? 'has' : 'have'} more than one invoice:{' '}
                    {summary.multi_invoice_cases.map((c, i) => (
                        <React.Fragment key={c}>
                            {i > 0 && ', '}
                            <button type="button" className="com__link-btn" onClick={() => onOpenCase(c)}>{c}</button>
                        </React.Fragment>
                    ))}. Payments fill a case's invoices in order; after importing, open the case to check where they
                    landed if the client may have paid two invoices at once.
                </p>
            )}

            {summary.unsplit_cases.length > 0 && (
                <>
                    <h3 className="com__section-title">Cases with an opener in Logics but no split agent here</h3>
                    <p className="mkt__panel-hint">
                        Their payments are imported either way. Add a split for any opener who should be paid on them;
                        the commission then counts every payment on the case, including these, and the settlement
                        officer's share shrinks to match.
                    </p>
                    <div className="com__table-wrap">
                        <table className="com__table">
                            <thead>
                                <tr>
                                    <th>Case #</th><th>Client</th><th>Opener</th>
                                    <th className="com__num">Payments</th><th className="com__num">Amount</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {summary.unsplit_cases.map((c) => (
                                    <tr key={c.case_number}>
                                        <td>{c.case_number}</td>
                                        <td>{c.client_name}</td>
                                        <td>{c.opener}</td>
                                        <td className="com__num">{c.payments}</td>
                                        <td className="com__num">{formatMoney(c.amount)}</td>
                                        <td>
                                            <button type="button" className="com__link-btn" onClick={() => onAddSplit(c)}>
                                                {c.entered ? 'Add split agent' : 'Enter case'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </>
    );
}

export default function Imports() {
    const navigate = useNavigate();
    const fileInput = useRef(null);
    const [file, setFile] = useState(null);
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [done, setDone] = useState(null);

    const [imports, setImports] = useState([]);
    const [payments, setPayments] = useState([]);
    const [listError, setListError] = useState(null);

    const loadLists = useCallback(async () => {
        setListError(null);
        try {
            const [i, p] = await Promise.all([fetchPaymentImports(), fetchCommissionPayments()]);
            setImports(i || []);
            setPayments(p || []);
        } catch (err) {
            setListError(err.message);
        }
    }, []);

    useEffect(() => { loadLists(); }, [loadLists]);

    const reset = () => {
        setFile(null);
        setSummary(null);
        setUploadError(null);
        if (fileInput.current) fileInput.current.value = '';
    };

    const handleFile = async (e) => {
        const picked = e.target.files && e.target.files[0];
        setDone(null);
        setSummary(null);
        setUploadError(null);
        setFile(picked || null);
        if (!picked) return;
        setBusy(true);
        try {
            setSummary(await previewPaymentReport(picked));
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleImport = async () => {
        setBusy(true);
        setUploadError(null);
        try {
            const result = await importPaymentReport(file);
            reset();
            setDone(result);
            await loadLists();
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const openCase = (caseNumber) => {
        navigate(`/commissions/splits?${new URLSearchParams({ case: caseNumber })}`);
    };

    const addSplit = (c) => {
        const params = new URLSearchParams({ case: c.case_number, opener: c.opener });
        navigate(`/commissions/splits?${params}`);
    };

    const importColumns = useMemo(() => [
        { field: 'uploaded_at', headerName: 'Uploaded', width: 170, valueFormatter: (p) => formatDateTime(p.value), sort: 'desc' },
        { field: 'file_name', headerName: 'File', minWidth: 200 },
        { field: 'uploaded_by', headerName: 'By', minWidth: 180 },
        { field: 'rows_total', headerName: 'Rows', type: 'rightAligned', width: 90 },
        { field: 'rows_new', headerName: 'New', type: 'rightAligned', width: 90 },
        { field: 'rows_updated', headerName: 'Updated', type: 'rightAligned', width: 100 },
        { field: 'rows_skipped', headerName: 'Skipped', type: 'rightAligned', width: 100 },
    ], []);

    const paymentColumns = useMemo(() => [
        { field: 'paid_at', headerName: 'Paid', width: 150, valueFormatter: (p) => formatPaidAt(p.value) },
        caseColumn(),
        { field: 'client_name', headerName: 'Client', minWidth: 160 },
        moneyColumn('amount', 'Amount'),
        { field: 'kind', headerName: 'Kind', width: 100 },
        { field: 'status', headerName: 'Status', width: 120 },
        { field: 'payment_method', headerName: 'Method', minWidth: 120 },
        { field: 'payment_type', headerName: 'Type', minWidth: 120 },
        { field: 'settlement_officer', headerName: 'Settlement Officer', minWidth: 160 },
        { field: 'opener', headerName: 'Opener (Logics)', minWidth: 140 },
        { field: 'authorization_code', headerName: 'Auth Code', minWidth: 120 },
    ], []);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 90 }), []);

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Payment Imports</h1>
                    <p className="page__subtitle">
                        Upload the IRS Logics payment report. Commission is calculated from the payments imported here.
                    </p>
                </div>
            </div>

            <div className="mkt__panel">
                <h2 className="mkt__panel-title">Upload a payment report</h2>
                <p className="mkt__panel-hint">
                    Export the payment report from Logics and pick the file (.csv or .xlsx). You'll see what's in it
                    before anything is saved. Overlapping date ranges are fine: payments already imported are not
                    counted again.
                </p>
                <div className="mkt__controls">
                    <label className="mkt__field mkt__field--grow">
                        <span>Report file</span>
                        <input
                            ref={fileInput}
                            type="file"
                            accept=".csv,.tsv,.txt,.xlsx"
                            onChange={handleFile}
                            disabled={busy}
                        />
                    </label>
                </div>

                {busy && <p className="page__subtitle">{summary ? 'Importing…' : 'Reading the file…'}</p>}
                {uploadError && <p className="mkt__error">{uploadError}</p>}

                {done && (
                    <p className="mkt__success">
                        Imported {done.file_name}: {done.import.rows_new} new
                        payment{done.import.rows_new === 1 ? '' : 's'}, {done.import.rows_updated} updated,
                        {' '}{done.import.rows_skipped} skipped.
                    </p>
                )}

                {summary && (
                    <>
                        <Summary summary={summary} onAddSplit={addSplit} onOpenCase={openCase} />
                        <div className="mkt__controls" style={{ marginBottom: 0 }}>
                            <button
                                className="blueButton"
                                onClick={handleImport}
                                disabled={busy || summary.new_rows + summary.known_rows === 0}
                            >
                                {summary.new_rows > 0
                                    ? `Import ${summary.new_rows} new row${summary.new_rows === 1 ? '' : 's'}`
                                    : 'Import (nothing new)'}
                            </button>
                            <button className="mkt__btn-secondary" onClick={reset} disabled={busy}>Cancel</button>
                        </div>
                    </>
                )}
            </div>

            {listError && <p className="mkt__error">{listError}</p>}

            <h2 className="mkt__panel-title" style={{ marginTop: 24 }}>Imported payments</h2>
            <p className="mkt__panel-hint">The newest 5,000 payments and refunds on file.</p>
            <DataGrid
                rowData={payments}
                columnDefs={paymentColumns}
                defaultColDef={defaultColDef}
                height={420}
                enableExport
                exportFileName={() => `Commission_Payments_${toISODate(new Date())}`}
            />

            <h2 className="mkt__panel-title" style={{ marginTop: 24 }}>Upload history</h2>
            <DataGrid
                rowData={imports}
                columnDefs={importColumns}
                defaultColDef={defaultColDef}
                height={300}
            />
        </div>
    );
}
